/**
 * Camada de IA no passo 7 da competência (03/09/2026; evolução 02/09/2026,
 * pedido do dono) — "Nosso × Deles × Avaliação da IA":
 *
 *  - SELETOR "Modelo da análise" ao lado do botão: populado com os modelos
 *    HABILITADOS na Administração (lista vazia = catálogo todo), default =
 *    modelo padrão da config; a escolha vale para a análise E para o chat
 *    (body `modelo`) e fica só em estado (não vai ao servidor);
 *  - CHECKBOX "Analisar também as já cientes/justificadas" → escopo "todas"
 *    (default "abertas"); o 422 "sem abertas" sugere marcar essa opção;
 *  - Botão "Analisar com IA": POST /competencias/:id/ia/analisar → o
 *    resumoGeral fica NO TOPO da seção e o detalhe por divergência passa a
 *    ENRIQUECER os cards das divergências (via onAnaliseCarregada — a faixa
 *    "Avaliação da IA" mora dentro de cada card, com match por id);
 *  - A IA PROPÕE, NUNCA APLICA: `correcao_v1` vira um envelope
 *    divergencias-correcoes@v1 injetado no fluxo de import JÁ EXISTENTE
 *    (dry-run com prévia — via onPrepararCorrecao da página);
 *  - Histórico "Análises anteriores" (GET …/ia/analises, sob demanda, com o
 *    escopo de cada análise);
 *  - Chat: botão FLUTUANTE (Affix) que abre o painel lateral (Drawer) —
 *    implementado em ia-chat-drawer.tsx.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  Divider,
  Group,
  Select,
  Spoiler,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconAlertTriangle, IconSparkles } from "@tabler/icons-react";
import { api, ErroApi } from "./api";
import { navegar } from "./roteador";
import { fmtDataHora } from "./format";
import { notificarErroApi } from "./componentes/notificacoes";
import type { ArquivoCorrecoes } from "./correcoes-import";
import { ChatIaFlutuante } from "./ia-chat-drawer";
import { FaixaAvaliacaoIa } from "./divergencias-cards";
import {
  custoAnaliseEstimado,
  fmtUsd,
  normalizarResultadoAnalise,
  type AnaliseIa,
  type ConfigIa,
  type ModeloIa,
  type ResultadoAnaliseIa,
} from "./ia-tipos";

export interface SecaoIaPasso7Props {
  competenciaId: number;
  /** total de divergências da competência (sem filtro de tela) — 0 desabilita a análise. */
  totalDivergencias: number;
  /** Injeta a correção proposta pela IA no fluxo de import existente (dry-run com prévia). */
  onPrepararCorrecao: (arquivo: ArquivoCorrecoes, nomeArquivo: string) => void;
  /** Entrega à página a análise mais recente NORMALIZADA (os cards das
   *  divergências ganham a faixa "Avaliação da IA" por match de id). */
  onAnaliseCarregada?: (resultado: ResultadoAnaliseIa | null) => void;
  /** 04/09/2026 — aplica a justificativa proposta pela IA no chat (clique
   *  humano; resolve true quando gravou — o card vira "Aplicada ✓"). */
  onAplicarJustificativa?: (divergenciaId: string, justificativa: string, referenciaEstudo?: string) => Promise<boolean>;
}

/** Erro estruturado do POST analisar (502 com raboDaResposta etc.). */
interface ErroAnalise {
  mensagem: string;
  rabo: string | null;
  status: number;
}

function erroAnaliseDe(e: unknown): ErroAnalise {
  if (e instanceof ErroApi) {
    const corpo = (e.corpo ?? {}) as { erro?: unknown; raboDaResposta?: unknown };
    return {
      mensagem: typeof corpo.erro === "string" ? corpo.erro : `Erro ${e.status} da API`,
      rabo: typeof corpo.raboDaResposta === "string" ? corpo.raboDaResposta : null,
      status: e.status,
    };
  }
  return { mensagem: e instanceof Error ? e.message : "Erro inesperado", rabo: null, status: 0 };
}

// ---------------------------------------------------------------------------
// Relatório de uma análise do HISTÓRICO (resumo + faixas compactas por item;
// a análise MAIS RECENTE enriquece os cards das divergências, não este bloco)
// ---------------------------------------------------------------------------

export function RelatorioAnaliseIa({
  analise,
  modelos,
  onPrepararCorrecao,
}: {
  analise: AnaliseIa;
  modelos: ModeloIa[] | null;
  onPrepararCorrecao?: (arquivo: ArquivoCorrecoes, nomeArquivo: string) => void;
}) {
  const resultado = normalizarResultadoAnalise(analise.resultado);
  const custo = custoAnaliseEstimado(modelos?.find((m) => m.id === analise.modelo) ?? null);
  return (
    <div>
      <Alert color="sillis" variant="light" title="Resumo da IA" mb="sm">
        <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{resultado.resumoGeral}</Text>
      </Alert>
      {resultado.divergencias.length === 0 && (
        <Text size="sm" c="dimmed" mb="sm">A IA não apontou divergências individuais nesta análise.</Text>
      )}
      {resultado.divergencias.map((d) => (
        <div key={d.id} style={{ marginBottom: 6 }}>
          <Group gap={6} mb={2}>
            <Text size="xs" fw={700}>{d.titulo}</Text>
            <Code>divergência {d.id}</Code>
          </Group>
          <FaixaAvaliacaoIa item={d} onPrepararCorrecao={onPrepararCorrecao} />
        </div>
      ))}
      <Text size="xs" c="dimmed" mt="xs">
        Análise de {fmtDataHora(analise.criadoEm)} · modelo {analise.modelo}
        {analise.escopo && <> · escopo: {analise.escopo === "todas" ? "todas as divergências" : "só as abertas"}</>}
        {analise.tokensPrompt !== null && analise.tokensResposta !== null && (
          <> · {analise.tokensPrompt.toLocaleString("pt-BR")} + {analise.tokensResposta.toLocaleString("pt-BR")} tokens (prompt/resposta)</>
        )}
        {custo !== null && <> · custo aproximado {fmtUsd(custo)}</>}
      </Text>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seção completa (seletor de modelo + botão + resumo + histórico + chat)
// ---------------------------------------------------------------------------

export function SecaoIaPasso7({ competenciaId, totalDivergencias, onPrepararCorrecao, onAnaliseCarregada, onAplicarJustificativa }: SecaoIaPasso7Props) {
  const [config, setConfig] = useState<ConfigIa | null>(null);
  const [modelos, setModelos] = useState<ModeloIa[] | null>(null);
  // 02/09/2026 — modelo escolhido na tela (estado local; vale p/ análise E chat)
  const [modeloEscolhido, setModeloEscolhido] = useState<string | null>(null);
  const [escopoTodas, setEscopoTodas] = useState(false);
  const [analisando, setAnalisando] = useState(false);
  const [analise, setAnalise] = useState<AnaliseIa | null>(null);
  const [erroAnalise, setErroAnalise] = useState<ErroAnalise | null>(null);
  const [infoAnalise, setInfoAnalise] = useState<string | null>(null);
  const [historico, setHistorico] = useState<AnaliseIa[] | null>(null);
  const [erroHistorico, setErroHistorico] = useState(false);

  const avisarAnalise = useCallback(
    (a: AnaliseIa | null) => {
      setAnalise(a);
      onAnaliseCarregada?.(a ? normalizarResultadoAnalise(a.resultado) : null);
    },
    [onAnaliseCarregada],
  );

  // GET /configuracoes/ia UMA vez ao montar o passo 7 (cacheado em estado);
  // a lista de modelos é melhor-esforço (catálogo do seletor + custo); a
  // análise MAIS RECENTE é carregada para já enriquecer os cards
  useEffect(() => {
    api.get<ConfigIa>("/configuracoes/ia")
      .then((c) => {
        setConfig(c);
        setModeloEscolhido((atual) => atual ?? c.modelo);
      })
      .catch(() => setConfig(null));
    api.get<{ total: number; modelos: ModeloIa[] }>("/ia/modelos").then((r) => setModelos(r.modelos)).catch(() => setModelos(null));
    api.get<{ total: number; analises: AnaliseIa[] }>(`/competencias/${competenciaId}/ia/analises`)
      .then((r) => {
        const lista = r.analises ?? [];
        if (lista.length > 0) avisarAnalise(lista[0]!);
      })
      .catch(() => undefined);
  }, [competenciaId, avisarAnalise]);

  const semChave = config !== null && !config.chaveConfigurada;
  const semDivergencias = totalDivergencias === 0;
  const bloqueado = semChave || semDivergencias;
  const motivoBloqueio = semChave
    ? "Configure a chave da OpenRouter na Administração para usar a análise com IA"
    : semDivergencias
      ? "Esta competência não tem divergências para analisar"
      : null;

  // 02/09/2026 — o seletor mostra os HABILITADOS (lista vazia = catálogo todo)
  const habilitados = config?.modelosHabilitados ?? [];
  const modelosVisiveis =
    habilitados.length > 0 ? (modelos ?? []).filter((m) => habilitados.includes(m.id)) : (modelos ?? []);
  const opcoesModelo = [
    ...modelosVisiveis.map((m) => ({ value: m.id, label: `${m.nome} — US$ ${m.precoPromptUsd1M}/US$ ${m.precoRespostaUsd1M} por 1M` })),
    ...(modeloEscolhido && !modelosVisiveis.some((m) => m.id === modeloEscolhido)
      ? [{ value: modeloEscolhido, label: `${modeloEscolhido} (configurado)` }]
      : []),
  ];
  const modeloEmUso = modeloEscolhido ?? config?.modelo ?? null;
  const custoEstimado = custoAnaliseEstimado(modelos?.find((m) => m.id === modeloEmUso) ?? null);
  // 05/09/2026 — modelo de RACIOCÍNIO em uso: a análise leva minutos, não
  // segundos (o timeout da análise é de 10 min por causa deles — erro real do
  // dono com Qwen3 Max Thinking em 05/2026)
  const modeloEhRaciocinio = modelos?.find((m) => m.id === modeloEmUso)?.raciocinio === true;

  async function analisar() {
    try {
      setAnalisando(true);
      setErroAnalise(null);
      setInfoAnalise(null);
      const r = await api.post<AnaliseIa>(`/competencias/${competenciaId}/ia/analisar`, {
        ...(modeloEmUso ? { modelo: modeloEmUso } : {}),
        escopo: escopoTodas ? "todas" : "abertas",
      });
      avisarAnalise(r);
      setHistorico(null); // invalida o histórico — a próxima abertura recarrega
    } catch (e) {
      const ea = erroAnaliseDe(e);
      if (ea.status === 422) setInfoAnalise(ea.mensagem);
      else setErroAnalise(ea);
      notificarErroApi(e);
    } finally {
      setAnalisando(false);
    }
  }

  async function carregarHistorico() {
    try {
      setErroHistorico(false);
      const r = await api.get<{ total: number; analises: AnaliseIa[] }>(`/competencias/${competenciaId}/ia/analises`);
      setHistorico(r.analises ?? []);
    } catch {
      setErroHistorico(true);
    }
  }

  const resultadoAtual = analise ? normalizarResultadoAnalise(analise.resultado) : null;

  return (
    <Card withBorder shadow="xs" mb="md" padding="sm">
      <Group gap="sm" mb="xs" wrap="wrap">
        <IconSparkles size={18} color="var(--mantine-color-sillis-7)" />
        <Title order={5}>Análise com IA — Nosso × Deles × Avaliação da IA</Title>
      </Group>
      <Group gap="sm" mb="xs" wrap="wrap" align="flex-end">
        <Select
          label="Modelo da análise"
          size="xs"
          w={300}
          searchable
          data={opcoesModelo}
          value={modeloEmUso}
          onChange={setModeloEscolhido}
          disabled={modelos === null}
          nothingFoundMessage="Nenhum modelo com esse nome"
          description={
            (modeloEhRaciocinio
              ? "Modelo de raciocínio — pensa antes de responder: a análise pode levar vários minutos. "
              : "") +
            (habilitados.length > 0
              ? "Lista curada na Administração — vale para a análise e o chat."
              : "Sem restrição na Administração — todos os modelos do catálogo.")
          }
        />
        <Tooltip label={motivoBloqueio ?? "Manda o dossiê das divergências para a IA e recebe propostas (nada é aplicado)"} disabled={!bloqueado} multiline w={280}>
          <span>
            <Button
              size="xs"
              color="sillis"
              leftSection={<IconSparkles size={14} />}
              disabled={bloqueado}
              loading={analisando}
              onClick={() => void analisar()}
            >
              Analisar com IA
            </Button>
          </span>
        </Tooltip>
        <Checkbox
          size="xs"
          label="Analisar também as já cientes/justificadas"
          checked={escopoTodas}
          onChange={(e) => setEscopoTodas(e.currentTarget.checked)}
          title="Por padrão a IA analisa só as divergências ABERTAS; marque para incluir as que já têm ciência"
        />
        {analisando && <Text size="xs" c="dimmed">A IA está lendo as divergências — {modeloEhRaciocinio ? "modelo de raciocínio: pode levar vários minutos…" : "pode levar alguns segundos…"}</Text>}
        {!analisando && custoEstimado !== null && !bloqueado && (
          <Text size="xs" c="dimmed">Custo estimado: ~{fmtUsd(custoEstimado)} por análise — a IA propõe, nunca aplica.</Text>
        )}
      </Group>
      {semChave && (
        <Alert color="yellow" variant="light" mb="xs" icon={<IconAlertTriangle size={16} />}>
          A análise com IA está indisponível até configurar a chave da OpenRouter na{" "}
          <Anchor onClick={() => navegar("/admin")} style={{ cursor: "pointer" }}>Administração</Anchor>.
        </Alert>
      )}
      {infoAnalise && (
        <Alert color="blue" variant="light" mb="xs" withCloseButton onClose={() => setInfoAnalise(null)}>
          {infoAnalise}
        </Alert>
      )}
      {erroAnalise && (
        <Alert color="red" variant="light" mb="xs" title="A análise com IA falhou" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm" mb={4}>{erroAnalise.mensagem}</Text>
          {erroAnalise.status === 400 && (
            <Text size="sm" mb={4}>
              Configure a chave na <Anchor onClick={() => navegar("/admin")} style={{ cursor: "pointer" }}>Administração</Anchor>.
            </Text>
          )}
          {erroAnalise.rabo && (
            <Spoiler maxHeight={40} showLabel="ver o fim da resposta da IA" hideLabel="esconder">
              <Code block>{erroAnalise.rabo}</Code>
            </Spoiler>
          )}
          <Button size="xs" variant="light" color="red" mt={6} onClick={() => void analisar()} loading={analisando}>
            Tentar de novo
          </Button>
        </Alert>
      )}
      {/* resumoGeral da análise mais recente NO TOPO — o detalhe por
          divergência mora nos cards (faixa "Avaliação da IA") */}
      {resultadoAtual && (
        <>
          <Divider my="xs" label="Análise mais recente — o detalhe aparece dentro de cada card de divergência" labelPosition="left" />
          <Alert color="sillis" variant="light" title="Resumo da IA" mb="xs">
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>{resultadoAtual.resumoGeral}</Text>
          </Alert>
          <Group gap="xs" mb="xs" wrap="wrap">
            <Badge variant="light" color="violet">{resultadoAtual.divergencias.length} divergência(s) com avaliação</Badge>
            {analise && (
              <Text size="xs" c="dimmed">
                {fmtDataHora(analise.criadoEm)} · modelo {analise.modelo}
                {analise.escopo && <> · escopo: {analise.escopo === "todas" ? "todas" : "só as abertas"}</>}
              </Text>
            )}
          </Group>
        </>
      )}
      <Accordion variant="separated" mt="xs">
        <Accordion.Item value="historico">
          <Accordion.Control onClick={() => { if (historico === null && !erroHistorico) void carregarHistorico(); }}>
            Análises anteriores{historico ? ` (${historico.length})` : ""}
          </Accordion.Control>
          <Accordion.Panel>
            <Text size="xs" c="dimmed" mb="xs">
              Recalcular a folha zera a lista de divergências e apaga as análises junto (regra do dono, 03/09/2026);
              re-importar/reconferir também invalida quando o quadro muda — em qualquer caso, rode a análise de novo.
            </Text>
            {erroHistorico && (
              <Alert color="yellow" variant="light" mb="xs" title="Não deu para carregar o histórico">
                <Button size="xs" variant="light" onClick={() => void carregarHistorico()}>Tentar de novo</Button>
              </Alert>
            )}
            {historico !== null && historico.length === 0 && (
              <Text size="sm" c="dimmed">Nenhuma análise anterior desta competência.</Text>
            )}
            {historico?.map((a) => (
              <Accordion key={a.id} variant="contained" mb={4}>
                <Accordion.Item value={a.id}>
                  <Accordion.Control>
                    <Group gap="xs">
                      <Text size="sm">
                        {fmtDataHora(a.criadoEm)} · {a.modelo}
                        {a.tokensPrompt !== null && a.tokensResposta !== null && (
                          <Text span size="xs" c="dimmed"> · {a.tokensPrompt + a.tokensResposta} tokens</Text>
                        )}
                      </Text>
                      {a.escopo && (
                        <Badge size="sm" variant="outline" color="gray">
                          {a.escopo === "todas" ? "todas" : "abertas"}
                        </Badge>
                      )}
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <RelatorioAnaliseIa analise={a} modelos={modelos} onPrepararCorrecao={onPrepararCorrecao} />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            ))}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      {/* 02/09/2026 — chat em painel LATERAL: botão flutuante (Affix) + Drawer */}
      <ChatIaFlutuante
        competenciaId={competenciaId}
        modelo={modeloEmUso}
        onPrepararCorrecao={onPrepararCorrecao}
        onAplicarJustificativa={onAplicarJustificativa}
      />
    </Card>
  );
}
